/**
 * Supabase API for Click&Share v2 Return App
 *
 * Provides functions for:
 * - Fetching bookings and items
 * - Loading merchant configurations
 * - Submitting return reports
 * - Uploading photos
 * - Managing return versions
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

// ============================================================================
// BOOKING OPERATIONS
// ============================================================================

/**
 * Fetch booking by order_id
 * Uses RPC function to bypass RLS on bookings table (with fallback to direct query)
 */
export const fetchBookingByOrderId = async (orderId) => {
  try {
    console.log('Fetching booking with order_id:', orderId);

    // Try RPC function first (works with RLS enabled)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_booking_by_order_id', { p_order_id: orderId });

    // If RPC function doesn't exist (PGRST202), fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('order_id', orderId)
        .single();

      if (error) {
        console.error('Error fetching booking:', error);
        throw error;
      }

      console.log('Booking found:', data);
      return data;
    }

    if (rpcError) {
      console.error('Error fetching booking:', rpcError);
      throw rpcError;
    }

    // RPC returns an array, get the first result
    const booking = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (!booking) {
      throw { code: 'PGRST116', message: 'No booking found with this order_id' };
    }

    console.log('Booking found:', booking);
    return booking;
  } catch (error) {
    console.error('Unexpected error in fetchBookingByOrderId:', error);
    throw error;
  }
};

/**
 * Fetch booking by booking ID (faster when ID is known)
 * Uses RPC function to bypass RLS on bookings table (with fallback to direct query)
 */
export const fetchBookingById = async (bookingId) => {
  try {
    console.log('Fetching booking with id:', bookingId);

    // Try RPC function first (works with RLS enabled)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_booking_by_id', { p_booking_id: bookingId });

    // If RPC function doesn't exist (PGRST202), fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (error) {
        console.error('Error fetching booking:', error);
        throw error;
      }

      console.log('Booking found:', data);
      return data;
    }

    if (rpcError) {
      console.error('Error fetching booking:', rpcError);
      throw rpcError;
    }

    // RPC returns an array, get the first result
    const booking = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (!booking) {
      throw { code: 'PGRST116', message: 'No booking found with this id' };
    }

    console.log('Booking found:', booking);
    return booking;
  } catch (error) {
    console.error('Unexpected error in fetchBookingById:', error);
    throw error;
  }
};

/**
 * Fetch booking items for a booking
 * Uses RPC function to bypass RLS on booking_items table (with fallback to direct query)
 */
export const fetchBookingItems = async (bookingId) => {
  try {
    console.log('Fetching booking items for booking:', bookingId);

    // Try RPC function first (works with RLS enabled)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_booking_items', { p_booking_id: bookingId });

    // If RPC function doesn't exist (PGRST202), fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('booking_items')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching booking items:', error);
        throw error;
      }

      console.log('Booking items found:', data?.length || 0);
      return data || [];
    }

    if (rpcError) {
      console.error('Error fetching booking items:', rpcError);
      throw rpcError;
    }

    console.log('Booking items found:', rpcData?.length || 0);
    return rpcData || [];
  } catch (error) {
    console.error('Unexpected error in fetchBookingItems:', error);
    throw error;
  }
};

/**
 * Fetch inventory items for booking items
 */
export const fetchInventoryItemsForBooking = async (bookingItems) => {
  try {
    if (!bookingItems || bookingItems.length === 0) {
      return [];
    }

    // Get all inventory_item_ids (filter out nulls)
    const inventoryItemIds = bookingItems
      .map(item => item.inventory_item_id)
      .filter(id => id !== null);

    if (inventoryItemIds.length === 0) {
      return [];
    }

    console.log('Fetching inventory items:', inventoryItemIds.length);

    const { data, error } = await supabase
      .from('inventory_items')
      .select('*')
      .in('id', inventoryItemIds);

    if (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }

    console.log('Inventory items found:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('Unexpected error in fetchInventoryItemsForBooking:', error);
    throw error;
  }
};

// ============================================================================
// CONFIGURATION OPERATIONS
// ============================================================================

/**
 * Fetch merchant data (for colors, name, etc.)
 */
export const fetchMerchant = async (merchantId) => {
  try {
    console.log('Fetching merchant:', merchantId);

    const { data, error } = await supabase
      .from('merchants')
      .select('id, merchant_id, business_name, display_name, color1, color2')
      .eq('id', merchantId)
      .single();

    if (error) {
      console.error('Error fetching merchant:', error);
      throw error;
    }

    console.log('Merchant found:', data?.business_name);
    return data;
  } catch (error) {
    console.error('Unexpected error in fetchMerchant:', error);
    throw error;
  }
};

/**
 * Fetch merchant configuration
 */
export const fetchMerchantConfig = async (merchantId) => {
  try {
    console.log('Fetching merchant config for:', merchantId);

    const { data, error } = await supabase
      .from('merchant_configurations')
      .select('*')
      .eq('merchant_id', merchantId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No configuration found, return null
        console.warn('No merchant configuration found for:', merchantId);
        return null;
      }
      console.error('Error fetching merchant config:', error);
      throw error;
    }

    console.log('Merchant config found');
    return data;
  } catch (error) {
    console.error('Unexpected error in fetchMerchantConfig:', error);
    throw error;
  }
};

/**
 * Fetch location data including Google Maps URL
 */
export const fetchLocation = async (locationId) => {
  try {
    console.log('Fetching location:', locationId);

    const { data, error } = await supabase
      .from('locations')
      .select('id, location_name, google_maps_url')
      .eq('id', locationId)
      .single();

    if (error) {
      console.error('Error fetching location:', error);
      throw error;
    }

    console.log('Location found:', data?.location_name);
    return data;
  } catch (error) {
    console.error('Unexpected error in fetchLocation:', error);
    throw error;
  }
};

// ============================================================================
// RETURN SUBMISSION OPERATIONS
// ============================================================================

/**
 * Check if a return already exists for a booking
 */
export const checkExistingReturn = async (bookingId) => {
  try {
    console.log('Checking for existing return:', bookingId);

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('check_existing_booking_return', { p_booking_id: bookingId });

    // If RPC function doesn't exist, fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('booking_returns')
        .select('*')
        .eq('booking_id', bookingId)
        .eq('is_latest', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking existing return:', error);
        throw error;
      }

      if (data) {
        console.log('Existing return found:', data.id);
      } else {
        console.log('No existing return found');
      }

      return data;
    }

    if (rpcError) {
      console.error('Error checking existing return:', rpcError);
      throw rpcError;
    }

    const existing = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (existing) {
      console.log('Existing return found:', existing.id);
    } else {
      console.log('No existing return found');
    }

    return existing;
  } catch (error) {
    console.error('Unexpected error in checkExistingReturn:', error);
    throw error;
  }
};

/**
 * Create a new booking return
 */
export const createBookingReturn = async (returnData) => {
  try {
    console.log('Creating booking return:', {
      booking_id: returnData.booking_id,
      submission_id: returnData.submission_id
    });

    const createdAtVienna = new Date().toLocaleString('en-CA', {
      timeZone: 'Europe/Vienna',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/,/g, '');

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('create_booking_return', {
        p_booking_id: returnData.booking_id,
        p_merchant_id: returnData.merchant_id,
        p_location_id: returnData.location_id,
        p_completed_sections: returnData.completed_sections || [],
        p_booking_level_answers: returnData.booking_level_answers || {},
        p_overall_status: returnData.overall_status,
        p_description: returnData.description || '',
        p_submission_id: returnData.submission_id,
        p_created_at_vienna: createdAtVienna
      });

    // If RPC function doesn't exist, fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct insert');
      const { data, error } = await supabase
        .from('booking_returns')
        .insert([{
          booking_id: returnData.booking_id,
          merchant_id: returnData.merchant_id,
          location_id: returnData.location_id,
          completed_sections: returnData.completed_sections || [],
          booking_level_answers: returnData.booking_level_answers || {},
          overall_status: returnData.overall_status,
          description: returnData.description,
          submission_id: returnData.submission_id,
          is_latest: true,
          created_at_vienna: createdAtVienna
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating booking return:', error);
        throw error;
      }

      console.log('Booking return created successfully:', data.id);
      return data;
    }

    if (rpcError) {
      console.error('Error creating booking return:', rpcError);
      throw rpcError;
    }

    // RPC returns an array, get the first result
    const bookingReturn = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (!bookingReturn) {
      throw new Error('No data returned from create_booking_return');
    }

    console.log('Booking return created successfully:', bookingReturn.id);
    return bookingReturn;
  } catch (error) {
    console.error('Unexpected error in createBookingReturn:', error);
    throw error;
  }
};

/**
 * Create booking item returns (item-level tracking)
 */
export const createBookingItemReturns = async (itemReturns) => {
  try {
    if (!itemReturns || itemReturns.length === 0) {
      console.log('No item returns to create');
      return [];
    }

    console.log('Creating booking item returns:', itemReturns.length);

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('create_booking_item_returns', { p_items: itemReturns });

    // If RPC function doesn't exist, fall back to direct insert
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct insert');
      const { data, error } = await supabase
        .from('booking_item_returns')
        .insert(itemReturns)
        .select();

      if (error) {
        console.error('Error creating booking item returns:', error);
        throw error;
      }

      console.log('Booking item returns created successfully:', data.length);
      return data;
    }

    if (rpcError) {
      console.error('Error creating booking item returns:', rpcError);
      throw rpcError;
    }

    console.log('Booking item returns created successfully:', rpcData?.length || 0);
    return rpcData || [];
  } catch (error) {
    console.error('Unexpected error in createBookingItemReturns:', error);
    throw error;
  }
};

/**
 * Upload photo to storage
 */
export const uploadPhoto = async (file, merchantId, bookingId, fileName) => {
  try {
    const filePath = `${merchantId}/${bookingId}/${fileName}`;

    console.log('Uploading photo:', filePath);

    const { data, error } = await supabase.storage
      .from('return-pictures')
      .upload(filePath, file, {
        contentType: 'image/jpeg',
        upsert: false
      });

    if (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }

    console.log('Photo uploaded successfully:', filePath);
    return { data, filePath };
  } catch (error) {
    console.error('Unexpected error in uploadPhoto:', error);
    throw error;
  }
};

/**
 * Create photo record in database
 */
export const createPhotoRecord = async (photoData) => {
  try {
    console.log('Creating photo record:', photoData.file_path);

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('create_booking_return_photo', {
        p_booking_return_id: photoData.booking_return_id,
        p_booking_id: photoData.booking_id,
        p_merchant_id: photoData.merchant_id,
        p_location_id: photoData.location_id,
        p_booking_item_return_id: photoData.booking_item_return_id || null,
        p_file_name: photoData.file_name,
        p_file_path: photoData.file_path
      });

    // If RPC function doesn't exist, fall back to direct insert
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct insert');
      const { data, error } = await supabase
        .from('booking_return_photos')
        .insert([{
          booking_return_id: photoData.booking_return_id,
          booking_id: photoData.booking_id,
          merchant_id: photoData.merchant_id,
          location_id: photoData.location_id,
          booking_item_return_id: photoData.booking_item_return_id || null,
          file_name: photoData.file_name,
          file_path: photoData.file_path
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating photo record:', error);
        throw error;
      }

      console.log('Photo record created successfully');
      return data;
    }

    if (rpcError) {
      console.error('Error creating photo record:', rpcError);
      throw rpcError;
    }

    const photoRecord = rpcData && rpcData.length > 0 ? rpcData[0] : null;
    console.log('Photo record created successfully');
    return photoRecord;
  } catch (error) {
    console.error('Unexpected error in createPhotoRecord:', error);
    throw error;
  }
};

/**
 * Upload photo and create record (convenience function)
 */
export const uploadPhotoComplete = async (file, photoData, fileName) => {
  try {
    // Upload to storage
    const { filePath } = await uploadPhoto(
      file,
      photoData.merchant_id,
      photoData.booking_id,
      fileName
    );

    // Create database record
    const record = await createPhotoRecord({
      ...photoData,
      file_name: fileName,
      file_path: filePath
    });

    return record;
  } catch (error) {
    console.error('Unexpected error in uploadPhotoComplete:', error);
    throw error;
  }
};

// ============================================================================
// COMPLETE SUBMISSION FLOW
// ============================================================================

/**
 * Submit complete return (booking + items + photos)
 */
export const submitCompleteReturn = async ({
  booking,
  merchantConfig,
  completedSections,
  bookingLevelAnswers,
  itemReturns,
  bookingPhotos,
  itemPhotos
}) => {
  try {
    console.log('Starting complete return submission');

    // 1. Check for existing return
    const existing = await checkExistingReturn(booking.id);
    if (existing) {
      console.log('Existing return found, marking it as not latest and creating new version');

      // Mark the existing return as not latest (use RPC to bypass RLS)
      const { error: rpcUpdateError } = await supabase
        .rpc('mark_booking_return_not_latest', { p_return_id: existing.id });

      // If RPC function doesn't exist, fall back to direct update
      if (rpcUpdateError && rpcUpdateError.code === 'PGRST202') {
        console.log('RPC function not found, falling back to direct update');
        const { error: updateError } = await supabase
          .from('booking_returns')
          .update({ is_latest: false })
          .eq('id', existing.id);

        if (updateError) {
          console.error('Error updating existing return:', updateError);
          throw updateError;
        }
      } else if (rpcUpdateError) {
        console.error('Error updating existing return:', rpcUpdateError);
        throw rpcUpdateError;
      }

      console.log('Previous return marked as not latest, proceeding with new submission');
    }

    // 2. Generate submission ID
    const submissionId = `${booking.order_id}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

    // 3. Determine overall status
    console.log('Determining overall status - itemReturns:', itemReturns);
    console.log('Item statuses:', itemReturns?.map(item => ({ id: item.booking_item_id, status: item.item_status })));

    const hasItemIssues = itemReturns?.some(item => item.item_status !== 'ok');
    const hasBookingIssues = Object.values(bookingLevelAnswers).some(value =>
      typeof value === 'string' && (
        value.includes('problem') ||
        value.includes('issue') ||
        value.includes('damage')
      )
    );

    console.log('Has item issues?', hasItemIssues);
    console.log('Has booking issues?', hasBookingIssues);

    const hasIssues = hasItemIssues || hasBookingIssues;
    const overallStatus = hasIssues ? 'issues_reported' : 'all_ok';

    console.log('Final overall status:', overallStatus);

    // 4. Create booking return
    const bookingReturn = await createBookingReturn({
      booking_id: booking.id,
      merchant_id: booking.merchant_id,
      location_id: booking.location_id,
      completed_sections: completedSections,
      booking_level_answers: bookingLevelAnswers,
      overall_status: overallStatus,
      description: '', // Will be generated from answers
      submission_id: submissionId
    });

    // 5. Create item returns (if any)
    let createdItemReturns = [];
    if (itemReturns && itemReturns.length > 0) {
      console.log('Creating item returns, count:', itemReturns.length);
      const itemReturnsWithReturnId = itemReturns.map(item => ({
        ...item,
        booking_return_id: bookingReturn.id,
        booking_id: booking.id
      }));
      console.log('Item returns with booking_return_id and booking_id:', itemReturnsWithReturnId);
      createdItemReturns = await createBookingItemReturns(itemReturnsWithReturnId);
      console.log('Created item returns:', createdItemReturns.length);
    } else {
      console.log('No item returns to create - itemReturns:', itemReturns);
    }

    // 6. Upload booking-level photos (if any)
    if (bookingPhotos && bookingPhotos.length > 0) {
      for (let i = 0; i < bookingPhotos.length; i++) {
        const photo = bookingPhotos[i];
        const fileName = `${booking.order_id}_${Date.now()}_${i}.jpg`;

        await uploadPhotoComplete(photo, {
          booking_return_id: bookingReturn.id,
          booking_id: booking.id,
          merchant_id: booking.merchant_id,
          location_id: booking.location_id,
          booking_item_return_id: null // Booking-level photo
        }, fileName);
      }
    }

    // 7. Upload item-level photos (if any)
    if (itemPhotos && Object.keys(itemPhotos).length > 0) {
      for (const [itemReturnId, photos] of Object.entries(itemPhotos)) {
        const itemReturn = createdItemReturns.find(ir => ir.id === itemReturnId);
        if (!itemReturn) continue;

        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          const fileName = `${booking.order_id}_item_${itemReturn.inventory_item_id || itemReturn.booking_item_id}_${Date.now()}_${i}.jpg`;

          await uploadPhotoComplete(photo, {
            booking_return_id: bookingReturn.id,
            booking_id: booking.id,
            merchant_id: booking.merchant_id,
            location_id: booking.location_id,
            booking_item_return_id: itemReturn.id
          }, fileName);
        }
      }
    }

    console.log('Complete return submission successful');

    return {
      status: 'success',
      bookingReturn,
      itemReturns: createdItemReturns
    };

  } catch (error) {
    console.error('Error in submitCompleteReturn:', error);
    throw error;
  }
};

// ============================================================================
// WEBHOOK (OPTIONAL - IF NEEDED)
// ============================================================================

/**
 * Call webhook for notification (optional)
 */
export const callWebhook = async (orderId, storeId, status) => {
  try {
    console.log('Calling webhook for:', { orderId, storeId, status });

    const response = await fetch('https://cloud.activepieces.com/api/v1/webhooks/ZQWjCysxyz4YAUgjMQSeb', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        store_id: storeId,
        status: status || 'completed'
      })
    });

    if (!response.ok) {
      throw new Error(`Webhook call failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Webhook called successfully:', result);
    return result;
  } catch (error) {
    console.error('Error calling webhook:', error);
    // Don't throw - webhook failure shouldn't block return submission
    return null;
  }
};

export default {
  supabase,
  fetchBookingByOrderId,
  fetchBookingItems,
  fetchInventoryItemsForBooking,
  fetchMerchant,
  fetchMerchantConfig,
  fetchLocation,
  checkExistingReturn,
  createBookingReturn,
  createBookingItemReturns,
  uploadPhoto,
  createPhotoRecord,
  uploadPhotoComplete,
  submitCompleteReturn,
  callWebhook
};
